import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { ConditionOption } from '@/lib/stores/dialogStore';
import { Input } from '@/components/ui/InputDefault';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { ConnectionBadge } from './field-editors/ConnectionBadge';

import { DialogVariable } from '@/lib/stores/dialogStore';
import { getTypeItemById } from '@/lib/utils/typeStoreResolver';

interface DialogConditionOptionEditorProps {
  option: ConditionOption;
  optionIndex: number;
  sectionIndex: number;
  fieldId: string;
  sectionIdentifiers: string[];
  sections?: { section_identifier: string; name: string }[];
  currentDialogId?: string;
  variableType: 'boolean' | 'number' | 'text' | 'range' | 'range-selector';
  variable?: DialogVariable;
  onUpdateOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<ConditionOption>) => void;
  onRemoveOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  gameId?: string;
  t: any;
}

export const DialogConditionOptionEditor = React.memo(function DialogConditionOptionEditor({
  option,
  optionIndex,
  sectionIndex,
  fieldId,
  sectionIdentifiers,
  sections,
  currentDialogId,
  variableType,
  variable,
  onUpdateOption,
  onRemoveOption,
  gameId,
  t,
}: DialogConditionOptionEditorProps) {
  const { selectedGame } = useSimplifiedUserData();
  const effectiveGameId = gameId || selectedGame?.id;

  // Get current section identifier
  const currentSectionIdentifier = React.useMemo(() => {
    if (!sections || sectionIndex >= sections.length) return undefined;
    return sections[sectionIndex]?.section_identifier;
  }, [sections, sectionIndex]);

  // Create a pseudo-field object for ConnectionBadge compatibility
  const pseudoField = React.useMemo(() => ({
    id: `condition-option-${optionIndex}`,
    next_field: option.next_field,
    next_section: option.next_section,
    next_dialog: option.next_dialog,
    next_dialog_section: option.next_dialog_section,
  }), [option.next_field, option.next_section, option.next_dialog, option.next_dialog_section, optionIndex]);

  // Wrapper function to handle updates from ConnectionBadge
  const handleConnectionUpdate = React.useCallback((sectionIdx: number, _fieldId: string, updates: any) => {
    onUpdateOption(sectionIndex, fieldId, optionIndex, updates);
  }, [onUpdateOption, sectionIndex, fieldId, optionIndex]);

  const sortableId = `condition-option-${sectionIndex}-${fieldId}-${optionIndex}`;
    
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getValueLabel = () => {
    if (variableType === 'boolean') {
      return option.condition_value === true ? 'TRUE' : 'FALSE';
    }
    if (variableType === 'range-selector' && option.operator === 'in_range') {
      // Get the range element name
      if (variable?.typeStoreRef?.typeId) {
        const typeItem = getTypeItemById(variable.typeStoreRef.typeId);
        const rangeElement = typeItem?.data?.find(el => el.id === String(option.condition_value));
        return `in ${rangeElement?.name || option.condition_value}`;
      }
      return `in_range ${option.condition_value}`;
    }
    const operator = option.operator || '==';
    return `${operator} ${option.condition_value}`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border rounded p-2 space-y-2 bg-background"
    >
      {/* Option Header */}
      <div className="flex items-center gap-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
          type="button"
        >
          <IconProvider iconstring="fa-grip-vertical" size={10} className="text-muted-foreground" />
        </button>
        <span className="text-xs font-medium font-mono">
          If {getValueLabel()}
        </span>
        {option.next_section && (
          <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto">
            <IconProvider iconstring="fa-arrow-right" size={8} />
            {sections?.find(s => s.section_identifier === option.next_section)?.name || option.next_section}
          </span>
        )}
        <Button
          onClick={() => onRemoveOption(sectionIndex, fieldId, optionIndex)}
          size="sm"
          variant="ghost"
          className="text-destructive h-6 w-6 p-0"
          type="button"
        >
          <IconProvider iconstring="fa-times" size={10} />
        </Button>
      </div>

      {/* Condition Value */}
      <div className="flex items-center gap-2">
        <Label className="text-xs whitespace-nowrap w-20">Value</Label>
        {variableType === 'range-selector' ? (
          /* Range selector - show range names */
          <div className="flex-1">
            {(() => {
              if (!variable?.typeStoreRef?.typeId) return <span className="text-xs text-gray-500">No ranges available</span>;
              const typeItem = getTypeItemById(variable.typeStoreRef.typeId);
              const ranges = typeItem?.data?.filter(el => el.valueType === 'range') || [];
              
              return (
                <select
                  className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={String(option.condition_value)}
                  onChange={(e) =>
                    onUpdateOption(sectionIndex, fieldId, optionIndex, {
                      condition_value: e.target.value,
                      operator: 'in_range',
                    })
                  }
                >
                  <option value="">Select range...</option>
                  {ranges.map((range) => (
                    <option key={range.id} value={range.id}>
                      {range.name} ({range.rangeMin ?? 0} - {range.rangeMax ?? 100})
                    </option>
                  ))}
                </select>
              );
            })()}
          </div>
        ) : variableType === 'boolean' ? (
          <select
            className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs font-mono"
            value={option.condition_value === true ? 'true' : 'false'}
            onChange={(e) =>
              onUpdateOption(sectionIndex, fieldId, optionIndex, {
                condition_value: e.target.value === 'true',
              })
            }
          >
            <option value="true">TRUE</option>
            <option value="false">FALSE</option>
          </select>
        ) : variableType === 'text' ? (
          <div className="flex-1 flex items-center gap-2">
            <select
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
              value={option.operator || '=='}
              onChange={(e) =>
                onUpdateOption(sectionIndex, fieldId, optionIndex, {
                  operator: e.target.value as any,
                })
              }
            >
              <option value="==">equals</option>
              <option value="!=">not equals</option>
              <option value="contains">contains</option>
              <option value="startsWith">starts with</option>
              <option value="endsWith">ends with</option>
            </select>
            <Input
              type="text"
              value={String(option.condition_value ?? '')}
              onChange={(e) =>
                onUpdateOption(sectionIndex, fieldId, optionIndex, {
                  condition_value: e.target.value,
                })
              }
              placeholder="text value"
              className="h-7 text-xs font-mono"
            />
          </div>
        ) : (
          /* Number or Range type */
          <div className="flex-1 flex items-center gap-2">
            <select
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
              value={option.operator || '=='}
              onChange={(e) =>
                onUpdateOption(sectionIndex, fieldId, optionIndex, {
                  operator: e.target.value as '==' | '!=' | '>' | '<' | '>=' | '<=',
                })
              }
            >
              <option value="==">==</option>
              <option value="!=">!=</option>
              <option value=">">&gt;</option>
              <option value="<">&lt;</option>
              <option value=">=">&gt;=</option>
              <option value="<=">&lt;=</option>
            </select>
            <Input
              type="number"
              value={typeof option.condition_value === 'number' ? option.condition_value : 0}
              onChange={(e) =>
                onUpdateOption(sectionIndex, fieldId, optionIndex, {
                  condition_value: parseFloat(e.target.value) || 0,
                })
              }
              placeholder="value"
              className="h-7 text-xs font-mono"
            />
          </div>
        )}
      </div>

      {/* Navigation Selection */}
      <div className="flex items-center gap-2">
        <Label className="text-xs font-semibold min-w-[40px]">Then:</Label>
        <ConnectionBadge
          field={pseudoField as any}
          sectionIndex={sectionIndex}
          sectionIdentifiers={sectionIdentifiers}
          sections={sections}
          currentDialogId={currentDialogId}
          effectiveGameId={effectiveGameId}
          onUpdateField={handleConnectionUpdate}
          showFieldOption={false}
        />
      </div>
    </div>
  );
});
